import { applyEase, type KeyframeEase } from './enter-exit-presets';

export const TEXT_STYLE_KEYFRAME_CHANNELS = [
  'color',
  'fontSize',
  'blendOpacity',
  'strokeColor',
  'strokeWidth',
  'backgroundColor',
  'backgroundOpacity',
  'backgroundRoundness',
  'backgroundWidth',
  'backgroundHeight',
  'backgroundOffsetX',
  'backgroundOffsetY',
  'bubbleColor',
  'bubbleOpacity',
  'glowColor',
  'glowStrength',
  'glowRange',
  'glowOffsetX',
  'glowOffsetY',
  'shadowColor',
  'shadowOpacity',
  'shadowBlur',
  'shadowDistance',
  'shadowAngle',
  'curve',
] as const;

export type TextStyleKeyframeChannel = (typeof TEXT_STYLE_KEYFRAME_CHANNELS)[number];
export type TextStyleKeyframeValue = number | string;
export type TextStyleKeyframeFrame = Partial<Record<TextStyleKeyframeChannel, TextStyleKeyframeValue>>;

export interface TextStyleKeyframe {
  /** Seconds from the clip start. */
  tSeconds: number;
  channel: TextStyleKeyframeChannel;
  value: TextStyleKeyframeValue;
  /** Easing from this point to the next point on the same channel. */
  ease?: KeyframeEase;
}

const TIME_EPSILON = 0.0005;

export function resolveTextStyleKeyframes(
  base: TextStyleKeyframeFrame,
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  clipRelativeTime: number,
): TextStyleKeyframeFrame {
  if (!keyframes?.length) return base;
  const resolved: TextStyleKeyframeFrame = { ...base };

  for (const channel of TEXT_STYLE_KEYFRAME_CHANNELS) {
    const points = keyframes
      .filter(point => point.channel === channel && validValue(point.value))
      .slice()
      .sort((a, b) => a.tSeconds - b.tSeconds);
    if (!points.length) continue;

    const first = points[0]!;
    if (clipRelativeTime + TIME_EPSILON < first.tSeconds) continue;
    if (points.length === 1 || clipRelativeTime >= points[points.length - 1]!.tSeconds - TIME_EPSILON) {
      resolved[channel] = points[points.length - 1]!.value;
      continue;
    }

    let left = first;
    let right = points[1]!;
    for (let index = 1; index < points.length; index++) {
      right = points[index]!;
      if (clipRelativeTime <= right.tSeconds + TIME_EPSILON) break;
      left = right;
    }
    const span = Math.max(TIME_EPSILON, right.tSeconds - left.tSeconds);
    const progress = Math.max(0, Math.min(1, (clipRelativeTime - left.tSeconds) / span));
    resolved[channel] = interpolateTextStyleValue(left.value, right.value, applyEase(progress, left.ease ?? 'linear'));
  }

  return resolved;
}

export function textStyleChannelHasKeyframes(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  channel: TextStyleKeyframeChannel,
): boolean {
  return Boolean(keyframes?.some(point => point.channel === channel));
}

export function textStyleKeyframeAt(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  channel: TextStyleKeyframeChannel,
  clipRelativeTime: number,
): TextStyleKeyframe | null {
  return keyframes?.find(point => point.channel === channel && Math.abs(point.tSeconds - clipRelativeTime) <= TIME_EPSILON) ?? null;
}

export function upsertTextStyleKeyframe(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  channel: TextStyleKeyframeChannel,
  clipRelativeTime: number,
  value: TextStyleKeyframeValue,
): TextStyleKeyframe[] {
  const next = (keyframes ?? []).map(point => ({ ...point }));
  const existing = next.find(point => point.channel === channel && Math.abs(point.tSeconds - clipRelativeTime) <= TIME_EPSILON);
  if (existing) {
    existing.tSeconds = roundTime(clipRelativeTime);
    existing.value = value;
  } else {
    next.push({ tSeconds: roundTime(clipRelativeTime), channel, value, ease: 'easeInOut' });
  }
  return sortTextStyleKeyframes(next);
}

export function removeTextStyleKeyframe(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  channel: TextStyleKeyframeChannel,
  clipRelativeTime: number,
): TextStyleKeyframe[] | null {
  const next = (keyframes ?? []).filter(point =>
    point.channel !== channel || Math.abs(point.tSeconds - clipRelativeTime) > TIME_EPSILON,
  );
  return next.length ? sortTextStyleKeyframes(next) : null;
}

export function removeTextStyleChannels(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  channels: readonly TextStyleKeyframeChannel[],
): TextStyleKeyframe[] | null {
  const removed = new Set(channels);
  const next = (keyframes ?? []).filter(point => !removed.has(point.channel));
  return next.length ? sortTextStyleKeyframes(next) : null;
}

export function removeTextStyleKeyframesAt(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  clipRelativeTime: number,
): TextStyleKeyframe[] | null {
  const next = (keyframes ?? []).filter(point => Math.abs(point.tSeconds - clipRelativeTime) > TIME_EPSILON);
  return next.length ? sortTextStyleKeyframes(next) : null;
}

export function textStyleEaseAt(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  clipRelativeTime: number,
): KeyframeEase {
  return keyframes?.find(point => Math.abs(point.tSeconds - clipRelativeTime) <= TIME_EPSILON)?.ease ?? 'linear';
}

export function setTextStyleEaseAt(
  keyframes: readonly TextStyleKeyframe[] | null | undefined,
  clipRelativeTime: number,
  ease: KeyframeEase,
): TextStyleKeyframe[] | null {
  if (!keyframes?.length) return null;
  return keyframes.map(point => Math.abs(point.tSeconds - clipRelativeTime) <= TIME_EPSILON
    ? { ...point, ease }
    : { ...point });
}

export function textStyleKeyframeId(point: Pick<TextStyleKeyframe, 'channel' | 'tSeconds'>): string {
  return `${point.channel}:${Math.round(point.tSeconds * 1000)}`;
}

function sortTextStyleKeyframes(points: TextStyleKeyframe[]): TextStyleKeyframe[] {
  return points.sort((a, b) => a.tSeconds - b.tSeconds || a.channel.localeCompare(b.channel));
}

function roundTime(value: number): number {
  return Math.round(Math.max(0, value) * 1000) / 1000;
}

function validValue(value: TextStyleKeyframeValue): boolean {
  return typeof value === 'string' ? value.trim().length > 0 : Number.isFinite(value);
}

function interpolateTextStyleValue(
  left: TextStyleKeyframeValue,
  right: TextStyleKeyframeValue,
  progress: number,
): TextStyleKeyframeValue {
  if (typeof left === 'number' && typeof right === 'number') {
    return left + (right - left) * progress;
  }
  if (typeof left === 'string' && typeof right === 'string') {
    return interpolateCssColor(left, right, progress) ?? (progress < 0.5 ? left : right);
  }
  return progress < 0.5 ? left : right;
}

function interpolateCssColor(left: string, right: string, progress: number): string | null {
  const a = parseCssColor(left);
  const b = parseCssColor(right);
  if (!a || !b) return null;
  const mix = (from: number, to: number): number => Math.round(from + (to - from) * progress);
  const red = mix(a.r, b.r);
  const green = mix(a.g, b.g);
  const blue = mix(a.b, b.b);
  const alpha = a.a + (b.a - a.a) * progress;
  if (Math.abs(alpha - 1) <= 0.0005) return `#${hex(red)}${hex(green)}${hex(blue)}`;
  return `rgba(${red}, ${green}, ${blue}, ${Math.round(alpha * 1000) / 1000})`;
}

function parseCssColor(value: string): { r: number; g: number; b: number; a: number } | null {
  const text = value.trim();
  const hexMatch = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);
  if (hexMatch) {
    const normalized = hexMatch[1]!.length === 3
      ? [...hexMatch[1]!].map(character => character + character).join('')
      : hexMatch[1]!;
    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
      a: 1,
    };
  }
  const rgbMatch = /^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)(?:\s*,\s*(\d+(?:\.\d+)?))?\s*\)$/i.exec(text);
  if (!rgbMatch) return null;
  return {
    r: clampByte(Number(rgbMatch[1])),
    g: clampByte(Number(rgbMatch[2])),
    b: clampByte(Number(rgbMatch[3])),
    a: Math.max(0, Math.min(1, rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]))),
  };
}

function clampByte(value: number): number {
  return Math.round(Math.max(0, Math.min(255, value)));
}

function hex(value: number): string {
  return clampByte(value).toString(16).padStart(2, '0');
}
